import { z } from "zod";
import { Operation } from "./operations.js";
import { Labels } from "./ssh-keys.js";

/*
 * Placement groups: servers of a "spread" group run on different physical
 * hosts, so one host failing takes down at most one of them. Free of charge.
 * A server joins only while powered off and belongs to one group at most.
 */

/** Servers a spread group may hold (Hetzner's limit). */
export const MAX_SERVERS_PER_PLACEMENT_GROUP = 10;

export const PlacementGroupKind = z.enum(["spread"]);
export type PlacementGroupKind = z.infer<typeof PlacementGroupKind>;

export const PlacementGroupName = z.string().trim().min(1).max(128);

export const PlacementGroup = z.object({
  id: z.uuid(),
  name: z.string(),
  kind: PlacementGroupKind,
  /** Its servers by our id and name (filled in by the API); id null when not in the copy. */
  servers: z.array(z.object({ id: z.uuid().nullable(), name: z.string() })),
  /** The provider's ids of its servers, as stored in the resource copy. */
  serverRefs: z.array(z.string()),
  labels: Labels,
  createdAt: z.iso.datetime(),
});
export type PlacementGroup = z.infer<typeof PlacementGroup>;

export const CreatePlacementGroupRequest = z.object({
  name: PlacementGroupName,
  kind: PlacementGroupKind.default("spread"),
  labels: Labels.default({}),
});
export type CreatePlacementGroupRequest = z.input<typeof CreatePlacementGroupRequest>;

export const UpdatePlacementGroupRequest = z
  .object({ name: PlacementGroupName.optional(), labels: Labels.optional() })
  .refine((v) => v.name !== undefined || v.labels !== undefined, "Nothing to change");
export type UpdatePlacementGroupRequest = z.infer<typeof UpdatePlacementGroupRequest>;

/** Adds a server to the group, or takes it out. */
export const PlacementGroupServerRequest = z.object({ serverId: z.uuid() });

/** Answer of adding or removing a server, which the provider finishes asynchronously. */
export const PlacementGroupChange = z.object({
  placementGroup: PlacementGroup,
  operation: Operation,
});
export type PlacementGroupChange = z.infer<typeof PlacementGroupChange>;
